import {
  createFileRoute,
  retainSearchParams,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useParams } from "@tanstack/react-router";
import { Stack, Text, Title } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { uploadCheckInPhoto } from "@/api/work-orders/checkIn/checkIn.queries";
import { VerticalStepper, type Step } from "@/components/ui/VerticalStepper/VerticalStepper";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal/ConfirmationModal";
import CodeOfConductStep from "@/components/features/WorkOrders/CheckIn/steps/CodeOfConductStep";
import AssessmentStep from "@/components/features/WorkOrders/CheckIn/steps/AssessmentStep";
import CheckInFooter, {
  type StepFooterConfig,
} from "@/components/features/WorkOrders/CheckIn/CheckInFooter";
import { INITIAL_CHECK_IN_STATE, type CheckInState } from "@/api/work-orders/checkIn/checkIn.types";
import {
  useCheckIn,
  useAgreeCodeOfConduct,
  useDeclineCheckIn,
  useSubmitCheckInPhotos,
} from "@/hooks/work-orders/checkIn/useCheckInQueries";
import styles from "@/components/features/WorkOrders/CheckIn/checkIn.module.scss";
import { showErrorAlert } from "@/utils/responseHandler";
import {
  leaveRecordTenant,
  RECORD_TENANT_SEARCH_FIELDS,
  syncRecordTenant,
  type RecordTenantSearch,
} from "@/utils/recordTenantRoute";

interface CheckInSearch extends RecordTenantSearch {
  step?: number;
}

export const Route = createFileRoute("/work-orders/$workOrderId/checkIn/")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): CheckInSearch => ({
    step: Number(search.step) || 1,
    tenant: RECORD_TENANT_SEARCH_FIELDS.tenant.parse(search.tenant),
  }),
  search: { middlewares: [retainSearchParams(["tenant"])] },
  onEnter: syncRecordTenant,
  onStay: syncRecordTenant,
  onLeave: leaveRecordTenant,
});

const checkInSteps = [
  { id: "1", stepKey: "code-of-conduct", label: "Code of Conduct" },
  { id: "2", stepKey: "arrival", label: "Arrival Photos" },
];

const STAGE_VALUE_TO_STEP: Record<string, number> = {
  code_of_conduct: 1,
  arrival_photos: 2,
};

function RouteComponent() {
  const { workOrderId } = useParams({ from: "/work-orders/$workOrderId/checkIn/" });
  const { step } = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const currentStep = step ?? 1;
  const [checkInState, setCheckInState] = useState<CheckInState>(INITIAL_CHECK_IN_STATE);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const stepInitialized = useRef(false);
  const { data: checkInData, isLoading: isCheckInLoading } = useCheckIn(workOrderId);
  const agreeCodeOfConductMutation = useAgreeCodeOfConduct(workOrderId);
  const declineCheckInMutation = useDeclineCheckIn(workOrderId);
  const submitPhotosMutation = useSubmitCheckInPhotos(workOrderId);

  // Sync step from backend stage value on first load
  useEffect(() => {
    if (!stepInitialized.current && checkInData?.checkIn.stage?.value) {
      stepInitialized.current = true;
      const mappedStep = STAGE_VALUE_TO_STEP[checkInData.checkIn.stage.value] ?? 1;
      if (mappedStep !== currentStep) {
        setCurrentStep(mappedStep);
      }
    }
  }, [checkInData?.checkIn.stage?.value]);

  const setCurrentStep = (nextStep: number) => {
    navigate({
      to: "/work-orders/$workOrderId/checkIn",
      params: { workOrderId },
      search: { step: nextStep },
      replace: true,
    });
  };

  const handleClose = () => {
    if (router.history.length > 1) {
      router.history.back();
    } else {
      navigate({ to: "/work-orders/$workOrderId", params: { workOrderId } });
    }
  };

  const getStepStatus = (stepIndex: number) => {
    if (stepIndex + 1 < currentStep) return "completed" as const;
    if (stepIndex + 1 === currentStep) return "current" as const;
    return "pending" as const;
  };

  const steps: Step[] = useMemo(
    () =>
      checkInSteps.map((step, index) => ({
        ...step,
        status: getStepStatus(index),
      })),
    [currentStep],
  );

  const handleNext = () => {
    if (currentStep < checkInSteps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDecline = () => {
    setIsDeclineModalOpen(true);
  };

  const handleConfirmDecline = useCallback(() => {
    declineCheckInMutation.mutate(undefined, {
      onSuccess: () => {
        setCheckInState((prev) => ({ ...prev, codeOfConduct: false }));
        setIsDeclineModalOpen(false);
        navigate({ to: "/work-orders", search: { tab: "activeWorkOrders" }, replace: true });
      },
    });
  }, []);

  const handleAccept = () => {
    agreeCodeOfConductMutation.mutate(undefined, {
      onSuccess: () => {
        setCheckInState((prev) => ({ ...prev, codeOfConduct: true }));
        handleNext();
      },
    });
  };

  const handleCompleteCheckin = () => {
    const requiredPhotos = ["uniform", "footwear", "vehicle"]
      .map((id) => {
        const photo = checkInState.photos[id];
        if (!photo?.s3Key) return null;
        return { s3Key: photo.s3Key, description: id };
      })
      .filter((photo): photo is { s3Key: string; description: string } => !!photo);

    const additionalPhotos = checkInState.additionalPhotos
      .filter((p) => !!p.s3Key)
      .map((p) => ({ s3Key: p.s3Key!, description: p.description ?? "" }));

    submitPhotosMutation.mutate(
      { photos: [...requiredPhotos, ...additionalPhotos] },
      {
        onSuccess: () => {
          setCheckInState((prev) => ({ ...prev, assessment: true }));
          handleClose();
        },
      },
    );
  };

  const handlePhotoUpload = useCallback(
    async (id: string, label: string, file: File) => {
      const checkInId = checkInData?.checkIn.id;
      if (!checkInId) return;

      const previewUrl = URL.createObjectURL(file);
      setCheckInState((prev) => ({
        ...prev,
        photos: { ...prev.photos, [id]: { id, file, previewUrl, label, isUploading: true } },
      }));

      try {
        const s3Key = await uploadCheckInPhoto(file, workOrderId, checkInId, id);
        setCheckInState((prev) => {
          const existing = prev.photos[id];
          if (!existing) return prev;
          return {
            ...prev,
            photos: { ...prev.photos, [id]: { ...existing, s3Key, isUploading: false } },
          };
        });
      } catch {
        URL.revokeObjectURL(previewUrl);
        setCheckInState((prev) => ({ ...prev, photos: { ...prev.photos, [id]: null } }));
        showErrorAlert("Failed to upload photo. Please try again.", "Upload failed");
      }
    },
    [checkInData?.checkIn.id, workOrderId],
  );

  const handlePhotoRemove = useCallback((id: string) => {
    setCheckInState((prev) => {
      const existing = prev.photos[id];
      if (existing) URL.revokeObjectURL(existing.previewUrl);
      return { ...prev, photos: { ...prev.photos, [id]: null } };
    });
  }, []);

  const handleAdditionalPhotoUpload = useCallback(
    async (file: File) => {
      const checkInId = checkInData?.checkIn.id;
      if (!checkInId) return;

      const photoId = `additional-${Date.now()}`;
      const previewUrl = URL.createObjectURL(file);

      setCheckInState((prev) => ({
        ...prev,
        additionalPhotos: [
          ...prev.additionalPhotos,
          { id: photoId, file, previewUrl, label: file.name, isUploading: true },
        ],
      }));

      try {
        const s3Key = await uploadCheckInPhoto(file, workOrderId, checkInId, photoId);
        setCheckInState((prev) => ({
          ...prev,
          additionalPhotos: prev.additionalPhotos.map((p) =>
            p.id === photoId ? { ...p, s3Key, isUploading: false } : p,
          ),
        }));
      } catch {
        URL.revokeObjectURL(previewUrl);
        setCheckInState((prev) => ({
          ...prev,
          additionalPhotos: prev.additionalPhotos.filter((p) => p.id !== photoId),
        }));
        showErrorAlert("Failed to upload photo. Please try again.", "Upload failed");
      }
    },
    [checkInData?.checkIn.id, workOrderId],
  );

  const handleAdditionalPhotoRemove = useCallback((id: string) => {
    setCheckInState((prev) => {
      const photo = prev.additionalPhotos.find((p) => p.id === id);
      if (photo) URL.revokeObjectURL(photo.previewUrl);
      return {
        ...prev,
        additionalPhotos: prev.additionalPhotos.filter((p) => p.id !== id),
      };
    });
  }, []);

  const allRequiredPhotosUploaded = useMemo(
    () => ["uniform", "footwear", "vehicle"].every((id) => !!checkInState.photos[id]?.s3Key),
    [checkInState.photos],
  );

  const isAnyPhotoUploading = useMemo(
    () =>
      ["uniform", "footwear", "vehicle"].some((id) => checkInState.photos[id]?.isUploading) ||
      checkInState.additionalPhotos.some((p) => p.isUploading),
    [checkInState.photos, checkInState.additionalPhotos],
  );

  const handleAdditionalPhotoDescriptionChange = useCallback((id: string, description: string) => {
    setCheckInState((prev) => ({
      ...prev,
      additionalPhotos: prev.additionalPhotos.map((p) => (p.id === id ? { ...p, description } : p)),
    }));
  }, []);

  const isCodeOfConductPending =
    agreeCodeOfConductMutation.isPending || declineCheckInMutation.isPending;

  const footerConfig: Record<number, StepFooterConfig> = useMemo(
    () => ({
      1: {
        rightButtons: [
          {
            label: "Disagree",
            onClick: handleDecline,
            isUnstyled: true,
            disabled: isCodeOfConductPending,
          },
          {
            label: "Agree",
            onClick: handleAccept,
            disabled: isCheckInLoading || isCodeOfConductPending,
          },
        ],
      },
      2: {
        rightButtons: [
          {
            label: "Previous",
            onClick: handlePrevious,
            variant: "transparent",
            color: "var(--text-default)",
          },
          {
            label: "Complete Check-in",
            onClick: handleCompleteCheckin,
            disabled:
              !allRequiredPhotosUploaded || isAnyPhotoUploading || submitPhotosMutation.isPending,
          },
        ],
      },
    }),
    [
      handleDecline,
      handleAccept,
      handleCompleteCheckin,
      handlePrevious,
      isCheckInLoading,
      isCodeOfConductPending,
      allRequiredPhotosUploaded,
      isAnyPhotoUploading,
      submitPhotosMutation.isPending,
    ],
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <CodeOfConductStep
            clientEntityName={checkInData?.codeOfConduct?.clientEntityName ?? ""}
            instructions={checkInData?.codeOfConduct.instructions ?? ""}
            isLoading={isCheckInLoading}
          />
        );
      case 2:
        return (
          <AssessmentStep
            workOrderId={workOrderId}
            photos={checkInState.photos}
            additionalPhotos={checkInState.additionalPhotos}
            onPhotoUpload={handlePhotoUpload}
            onPhotoRemove={handlePhotoRemove}
            onAdditionalPhotoUpload={handleAdditionalPhotoUpload}
            onAdditionalPhotoRemove={handleAdditionalPhotoRemove}
            onAdditionalPhotoDescriptionChange={handleAdditionalPhotoDescriptionChange}
          />
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Title order={3}>Check-in</Title>
        <IconX size={16} onClick={handleClose} cursor="pointer" aria-label="Close" />
      </div>

      {/* Body - with Stepper and Content */}
      <div className={styles.body}>
        <div className={styles.stepper}>
          <VerticalStepper steps={steps} />
        </div>

        {/* Content Column */}
        <div className={styles.content}>
          <Stack gap="lg">{renderStepContent()}</Stack>
        </div>
      </div>

      {/* Footer - Full Width at Bottom */}
      <div className={styles.footer}>
        <CheckInFooter currentStep={currentStep} footerConfig={footerConfig} />
      </div>

      {/* Decline Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeclineModalOpen}
        onClose={() => setIsDeclineModalOpen(false)}
        title="Decline this Work Order"
        body={
          <>
            <Text>
              This will remove the work order from your schedule and make you ineligible for future
              opportunities from {checkInData?.codeOfConduct?.clientEntityName ?? ""}
            </Text>
            <Text>This action cannot be undone.</Text>
          </>
        }
        cancelText="Cancel"
        confirmText="Decline Work Order"
        confirmVariant="destructive-primary"
        onConfirm={handleConfirmDecline}
      />
    </div>
  );
}
